import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TodosService } from '../todos.service';
import { Priority } from 'src/app/helpers/Interfaces/todo';
import { DataService } from 'src/app/helpers/data.service';
import { format } from 'date-fns';

@Component({
  selector: 'app-reminder-modal',
  templateUrl: './reminder-modal.component.html',
  styleUrls: ['./reminder-modal.component.scss'],
})
export class ReminderModalComponent {
  priorityLabel: string = 'Unknown';
  currentUserId = '';
  isMarkingAsDone = false;
  isSettingReminder = false;
  showDateTimePicker = false;

  // Date/time picker properties
  selectedDate: string = '';
  selectedTime: string = '';
  minDate: string;

  // Default options for quick selection
  quickOptions = [
    { label: 'In 5 minutes', minutes: 5 },
    { label: 'In 15 minutes', minutes: 15 },
    { label: 'In 30 minutes', minutes: 30 },
    { label: 'In 1 hour', minutes: 60 },
    { label: 'In 2 hours', minutes: 120 },
    { label: 'Tomorrow same time', days: 1 },
  ];

  constructor(
    private _dataServ: DataService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ReminderModalComponent>,
    private todosService: TodosService
  ) {
    this.priorityLabel = this.getPriorityLabel(Number(data.priority));
    this.currentUserId = this._dataServ.employeeValue?.id ?? '';

    // Set minimum date to today
    const today = new Date();
    this.minDate = today.toISOString().split('T')[0];

    // Initialize date/time to 5 minutes from now
    this.initializeDefaultDateTime();
  }

  private initializeDefaultDateTime(): void {
    const now = new Date();
    const fiveMinutesLater = new Date(now.getTime() + 5 * 60000);

    this.selectedDate = fiveMinutesLater.toISOString().split('T')[0];
    this.selectedTime = this.formatTimeForInput(fiveMinutesLater);
  }

  private formatTimeForInput(date: Date): string {
    return date.toTimeString().slice(0, 5); // HH:mm format
  }

  close() {
    this.dialogRef.close();
  }

  markAsDone() {
    if (!this.currentUserId) {
      console.error('User ID not available');
      return;
    }

    if (this.isMarkingAsDone) return;

    this.isMarkingAsDone = true;

    this.todosService.markAsDone(this.data.id, this.currentUserId).subscribe({
      next: () => {
        this.todosService.clearReminder(this.data.id);
        this.dialogRef.close('markedAsDone');
      },
      error: (error) => {
        console.error('Error marking todo as done:', error);
        this.isMarkingAsDone = false;
      },
      complete: () => {
        this.isMarkingAsDone = false;
      },
    });
  }

  toggleDateTimePicker() {
    this.showDateTimePicker = !this.showDateTimePicker;
  }

  selectQuickOption(option: any) {
    const now = new Date();
    let remindDate: Date;

    if (option.minutes) {
      remindDate = new Date(now.getTime() + option.minutes * 60000);
    } else if (option.days) {
      remindDate = new Date(now);
      remindDate.setDate(now.getDate() + option.days);
      // Keep the same time
      remindDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
    } else {
      remindDate = new Date(now.getTime() + 5 * 60000); // Default 5 minutes
    }

    this.selectedDate = remindDate.toISOString().split('T')[0];
    this.selectedTime = this.formatTimeForInput(remindDate);

    // Show the date/time picker so user can see the selected time
    this.showDateTimePicker = true;
  }

  remindMeLater() {
    if (!this.currentUserId) {
      console.error('User ID not available');
      return;
    }

    if (this.isSettingReminder) return;

    // Validate date and time
    if (!this.selectedDate || !this.selectedTime) {
      console.error('Please select date and time');
      return;
    }

    // Combine date and time
    const dateTimeString = `${this.selectedDate}T${this.selectedTime}:00`;
    const remindDateTime = new Date(dateTimeString);

    // Validate that the date is in the future
    if (remindDateTime <= new Date()) {
      console.error('Please select a future date and time');
      return;
    }

    // Format to ISO string as required by API
    const isoDateTime = remindDateTime.toISOString();

    this.isSettingReminder = true;

    this.todosService
      .remindLater(this.data.id, this.currentUserId, isoDateTime)
      .subscribe({
        next: () => {
          this.todosService.clearReminder(this.data.id);
          this.dialogRef.close('remindLater');
        },
        error: (error) => {
          console.error('Error setting reminder:', error);
          this.isSettingReminder = false;
        },
        complete: () => {
          this.isSettingReminder = false;
        },
      });
  }

  getPriorityLabel(priority: number): string {
    switch (priority) {
      case Priority.Low:
        return 'Low';
      case Priority.Medium:
        return 'Medium';
      case Priority.High:
        return 'High';
      default:
        return 'Unknown';
    }
  }

  getPriorityBadgeClass(priority: number): string {
    switch (priority) {
      case Priority.Low:
        return 'bg-success';
      case Priority.Medium:
        return 'bg-warning text-dark';
      case Priority.High:
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }
}
